import React, { useEffect, useState, useContext } from 'react';
import axiosInstance from '../axiosConfig';
import { buildMediaUrl } from '../apiConfig';
import { AuthContext } from '../AuthContext';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { hasGroup } from '../utils/permissions';
import './UpgradeDetails.css';

function UpgradeDetails({ upgrade, onUpdateCart }) {
  const { selectedServiceCenter, user } = useContext(AuthContext);
  const [upgradeData, setUpgradeData] = useState(upgrade);
  const [quantities, setQuantities] = useState({});
  const [incompleteComponents, setIncompleteComponents] = useState([]);
  const [canIssue, setCanIssue] = useState(false);

  const imageUrl = upgradeData.photo ? buildMediaUrl(upgradeData.photo) : null;
  const isStorekeeper = hasGroup(user, 'Кладовщик');

  useEffect(() => {
    const initialQuantities = {};
    let incompleteList = [];
    let canIssueFlag = false;

    upgrade.components.forEach(component => {
      initialQuantities[component.id] = component.quantity_in_upgrade || 0;
      if (component.quantity <= 0 || component.quantity_in_upgrade > component.quantity) {
        incompleteList.push(component.name);
      }
      if (component.quantity > 0) {
        canIssueFlag = true;
      }
    });

    setQuantities(initialQuantities);
    setIncompleteComponents(incompleteList);
    setCanIssue(canIssueFlag);
  }, [upgrade]);

  useEffect(() => {
    setUpgradeData(upgrade);
  }, [upgrade]);

  const showToast = (message, type) => {
    const bg = type === 'danger'
      ? 'linear-gradient(to right, #dc3545, #f46b6b)'
      : 'linear-gradient(to right, #28a745, #58d18b)';
    window.Toastify({
      text: message,
      duration: 3000,
      gravity: "top",
      position: "right",
      style: { background: bg },
      stopOnFocus: true,
      className: 'custom-toast',
    }).showToast();
  };

  // Обработка изменения количества компонентов
  const handleQuantityChange = (componentId, newQuantity) => {
    setQuantities(prevQuantities => ({
      ...prevQuantities,
      [componentId]: parseInt(newQuantity, 10) || 0
    }));
  };

  // Добавление апгрейда в корзину
  const handleAddToCart = async () => {
    // Предварительная проверка на фронте (реальная проверка всё равно на бэке)
    const allComponentsAvailable = upgradeData.components.every(component =>
      (quantities[component.id] || 0) <= (component.quantity || 0)
    );

    if (!allComponentsAvailable) {
      showToast('Не все компоненты доступны в указанном количестве.', 'danger');
      return;
    }

    try {
      const resp = await axiosInstance.post('/api/cart/add/', {
        upgrade_id: upgradeData.id,
        quantities: quantities
      });

      if (resp?.data?.success) {
        // Оптимистично уменьшим локальные остатки
        setUpgradeData(prev => ({
          ...prev,
          components: prev.components.map(c => {
            const addQty = quantities[c.id] || 0;
            return addQty > 0 ? { ...c, quantity: Math.max(0, (c.quantity || 0) - addQty) } : c;
          })
        }));
        onUpdateCart && onUpdateCart();
        showToast('Апгрейд успешно добавлен в корзину.', 'success');
        return;
      }

      showToast('Не удалось добавить апгрейд в корзину.', 'danger');
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      // 409 + shortages при нехватке
      if (status === 409 && data?.reason === 'out_of_stock' && Array.isArray(data?.shortages)) {
        const shortageMap = new Map(
          data.shortages.map(s => [s.component_id, (Number.isFinite(s.available) ? s.available : (s.component?.quantity ?? 0))])
        );

        setUpgradeData(prev => ({
          ...prev,
          components: prev.components.map(c => {
            if (shortageMap.has(c.id)) {
              return { ...c, quantity: shortageMap.get(c.id) };
            }
            return c;
          })
        }));

        const names = data.shortages.map(s => s.name || s.component_id).join(', ');
        const msg = data.message || `Недостаточно компонентов: ${names}`;
        showToast(msg, 'danger');
        return;
      }

      console.error('Error adding upgrade to cart:', error);
      showToast('Ошибка при добавлении апгрейда в корзину.', 'danger');
    }
  };

  return (
    <div className="container mt-4 upgrade">
      <div className="row">
        <div className="col-md-6 text-center">
          {imageUrl && (
            <div className="image-slider position-relative">
              <img src={imageUrl} alt={upgradeData.name} className="component-image img-fluid" />
              {incompleteComponents.length > 0 && (
                <div className="watermark">
                  Не комплект
                </div>
              )}
            </div>
          )}
        </div>
        <div className="col-md-6">
          <h1>{upgradeData.name}</h1>
          <p>{upgradeData.description}</p>
          <h2>Компоненты</h2>
          <div className="components-list">
            {upgradeData.components.map(component => {
              const isOverallocated = component.quantity_in_upgrade > component.quantity;
              return (
                <div
                  key={component.id}
                  className={`component-details-upgrade ${isOverallocated ? 'text-danger' : ''}`}
                >
                  <span>
                    {component.name} ({component.quantity_in_upgrade} шт. в апгрейде, {component.quantity} шт. на складе)
                  </span>
                  {isStorekeeper && (
                    <input
                      type="number"
                      min="0"
                      value={quantities[component.id] ?? ''} // контролируемое значение
                      onChange={(e) => handleQuantityChange(component.id, e.target.value)}
                      className="form-control mt-2"
                      placeholder="Количество"
                    />
                  )}
                </div>
              );
            })}
          </div>
          {!isStorekeeper && (
            <div className="action-buttons mt-3">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-add-to-cart">Добавить в заказ</Tooltip>}
              >
                <button className="btn btn-primary" onClick={handleAddToCart}>
                  <i className="fas fa-cart-plus"></i>
                </button>
              </OverlayTrigger>
            </div>
          )}
          {isStorekeeper && (
            <div className="action-buttons mt-3">
              {canIssue && (
                <>
                  {/* Ваши действия с апгрейдом закомментированы */}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UpgradeDetails;
